import { useTranslation } from 'react-i18next';
import {
  FiArrowRight,
  FiArrowUpRight,
  FiCheck,
  FiCode,
  FiGlobe,
  FiLayers,
  FiTrendingUp,
  FiZap,
} from 'react-icons/fi';
import '../../growth.css';

const copyByLanguage = {
  pt: {
    eyebrow: 'AI PARA CRESCIMENTO EMPRESARIAL',
    title: 'Construímos sistemas digitais que fazem o seu negócio avançar.',
    intro:
      'Automação, websites, aplicações e marketing inteligente conectados à operação da sua empresa — para reduzir trabalho manual, melhorar a experiência do cliente e crescer com mais controlo.',
    primaryCta: 'Falar sobre um projeto',
    secondaryCta: 'Conhecer soluções',
    proof: ['Estratégia antes da tecnologia', 'Soluções feitas para a operação real', 'AI aplicada onde gera valor'],
    problemEyebrow: 'COMEÇAMOS PELO PROBLEMA',
    problemTitle: 'O que você quer melhorar no seu negócio?',
    problemIntro:
      'Não começamos por uma ferramenta. Primeiro entendemos o que precisa funcionar melhor e desenhamos a tecnologia ao redor disso.',
    solutions: [
      {
        title: 'Automatizar processos',
        description: 'Elimine tarefas repetitivas, conecte sistemas e crie fluxos que continuam a trabalhar mesmo quando a equipa está ocupada.',
        label: 'Automação & AI',
      },
      {
        title: 'Criar um website que vende',
        description: 'Websites rápidos, claros e conectados ao seu funil comercial, CRM, WhatsApp, formulários e automações.',
        label: 'Websites',
      },
      {
        title: 'Construir uma aplicação',
        description: 'Portais, dashboards e sistemas personalizados quando as ferramentas prontas já não acompanham a sua operação.',
        label: 'Apps & Sistemas',
      },
      {
        title: 'Atrair mais clientes',
        description: 'Conteúdo, tráfego pago e decisões de marketing organizadas por estratégia, dados e execução.',
        label: 'Growth & Marketing',
      },
    ],
    systemEyebrow: 'UM SISTEMA, NÃO PEÇAS SOLTAS',
    systemTitle: 'Tudo pode trabalhar junto.',
    systemIntro:
      'Marketing, website, atendimento e operação não precisam viver em ferramentas isoladas. Quando faz sentido, desenhamos o fluxo completo.',
    flow: ['Anúncio', 'Website', 'Lead', 'CRM', 'Automação', 'WhatsApp / Email', 'Agendamento', 'Follow-up'],
    capabilitiesEyebrow: 'O QUE CONSTRUÍMOS',
    capabilitiesTitle: 'Tecnologia aplicada a crescimento e eficiência.',
    capabilities: [
      ['Automação & AI', 'Agentes, integrações, atendimento, follow-up, CRM, workflows internos e operações assistidas por AI.'],
      ['Websites', 'Sites institucionais, landing pages, experiências de conversão, SEO técnico e integrações com a operação.'],
      ['Apps personalizados', 'SaaS, portais de cliente, dashboards, ferramentas internas e aplicações web feitas à medida.'],
      ['Growth & Ads', 'Estratégia de conteúdo, Social Manager, Google Ads, Meta Ads, análise e melhoria contínua.'],
    ],
    productEyebrow: 'PRODUTO MAIPRO',
    productTitle: 'Social Manager. Da estratégia à execução.',
    productDescription:
      'A nossa própria plataforma organiza objetivos de negócio, plano de conteúdo, campanhas, aprovações e decisões de marketing num único fluxo.',
    productPoints: ['Planeamento orientado por objetivo', 'Conteúdo e campanhas no mesmo fluxo', 'Aprovação antes de publicar', 'Leitura de desempenho para decidir o próximo passo'],
    productCta: 'Conhecer o Social Manager',
    casesEyebrow: 'COMO PENSAMOS',
    casesTitle: 'Menos entregáveis. Mais sistemas úteis.',
    cases: [
      ['Captação conectada', 'Anúncios e conteúdo levam para experiências digitais que capturam, qualificam e encaminham cada oportunidade.'],
      ['Operação automatizada', 'Leads, mensagens, formulários, agenda e tarefas internas podem seguir um fluxo consistente sem depender de trabalho manual.'],
      ['Software próprio', 'Quando uma ferramenta pronta não resolve o processo, desenhamos uma aplicação específica para a empresa.'],
    ],
    processEyebrow: 'COMO TRABALHAMOS',
    processTitle: 'Do problema ao sistema em quatro passos.',
    process: [
      ['01', 'Entendemos', 'Mapeamos o negócio, o objetivo e o ponto onde existe fricção ou oportunidade.'],
      ['02', 'Desenhamos', 'Definimos experiência, fluxo, dados e tecnologia antes de construir.'],
      ['03', 'Construímos', 'Desenvolvemos, integramos e simplificamos o uso para a equipa e para o cliente.'],
      ['04', 'Melhoramos', 'Medimos utilização e resultado para evoluir o que realmente importa.'],
    ],
    aiEyebrow: 'A NOSSA VISÃO SOBRE AI',
    aiTitle: 'AI quando faz sentido.',
    aiDescription:
      'Não adicionamos inteligência artificial apenas para dizer que usamos AI. Aplicamos modelos, automações e agentes quando eles tornam um processo mais rápido, uma experiência melhor ou uma decisão mais inteligente.',
    finalTitle: 'Tem algo no seu negócio que poderia funcionar melhor?',
    finalDescription:
      'Conte-nos o problema. Nós ajudamos a descobrir se a melhor resposta é automação, website, software, marketing — ou uma combinação disso tudo.',
    finalCta: 'Conversar com a MAIPRO',
  },
  en: {
    eyebrow: 'AI FOR BUSINESS GROWTH',
    title: 'We build digital systems that move your business forward.',
    intro:
      'Automation, websites, custom applications and intelligent marketing connected to your operation — to reduce manual work, improve customer experience and grow with more control.',
    primaryCta: 'Discuss a project',
    secondaryCta: 'Explore solutions',
    proof: ['Strategy before technology', 'Built around real operations', 'AI applied where it creates value'],
    problemEyebrow: 'WE START WITH THE PROBLEM',
    problemTitle: 'What do you want to improve in your business?',
    problemIntro: 'We do not start with a tool. We first understand what needs to work better and design technology around it.',
    solutions: [
      { title: 'Automate processes', description: 'Remove repetitive work, connect systems and create flows that keep moving while your team is busy.', label: 'Automation & AI' },
      { title: 'Build a website that sells', description: 'Fast, clear websites connected to your sales funnel, CRM, WhatsApp, forms and automations.', label: 'Websites' },
      { title: 'Build an application', description: 'Portals, dashboards and custom systems when off-the-shelf tools no longer fit your operation.', label: 'Apps & Systems' },
      { title: 'Attract more customers', description: 'Content, paid media and marketing decisions organised around strategy, data and execution.', label: 'Growth & Marketing' },
    ],
    systemEyebrow: 'ONE SYSTEM, NOT DISCONNECTED PIECES',
    systemTitle: 'Everything can work together.',
    systemIntro: 'Marketing, website, service and operations do not need to live in isolated tools. When it makes sense, we design the entire flow.',
    flow: ['Ad', 'Website', 'Lead', 'CRM', 'Automation', 'WhatsApp / Email', 'Booking', 'Follow-up'],
    capabilitiesEyebrow: 'WHAT WE BUILD',
    capabilitiesTitle: 'Technology applied to growth and efficiency.',
    capabilities: [
      ['Automation & AI', 'Agents, integrations, service, follow-up, CRM, internal workflows and AI-assisted operations.'],
      ['Websites', 'Corporate sites, landing pages, conversion experiences, technical SEO and operational integrations.'],
      ['Custom apps', 'SaaS, client portals, dashboards, internal tools and tailored web applications.'],
      ['Growth & Ads', 'Content strategy, Social Manager, Google Ads, Meta Ads, analytics and continuous improvement.'],
    ],
    productEyebrow: 'MAIPRO PRODUCT',
    productTitle: 'Social Manager. From strategy to execution.',
    productDescription: 'Our own platform connects business goals, content planning, campaigns, approvals and marketing decisions in one workflow.',
    productPoints: ['Goal-led planning', 'Content and campaigns in one workflow', 'Approval before publishing', 'Performance insight to decide what comes next'],
    productCta: 'Explore Social Manager',
    casesEyebrow: 'HOW WE THINK',
    casesTitle: 'Fewer deliverables. More useful systems.',
    cases: [
      ['Connected acquisition', 'Ads and content lead to digital experiences that capture, qualify and route every opportunity.'],
      ['Automated operations', 'Leads, messages, forms, booking and internal tasks can follow a consistent flow without depending on manual work.'],
      ['Own software', 'When an off-the-shelf tool cannot solve the process, we design an application around the business.'],
    ],
    processEyebrow: 'HOW WE WORK',
    processTitle: 'From problem to system in four steps.',
    process: [
      ['01', 'Understand', 'We map the business, objective and point where friction or opportunity exists.'],
      ['02', 'Design', 'We define experience, flow, data and technology before building.'],
      ['03', 'Build', 'We develop, integrate and simplify usage for both the team and the customer.'],
      ['04', 'Improve', 'We measure usage and outcomes to evolve what actually matters.'],
    ],
    aiEyebrow: 'OUR VIEW ON AI',
    aiTitle: 'AI when it makes sense.',
    aiDescription: 'We do not add artificial intelligence just to say we use AI. We apply models, automations and agents when they make a process faster, an experience better or a decision smarter.',
    finalTitle: 'Is there something in your business that should work better?',
    finalDescription: 'Tell us the problem. We will help determine whether the best answer is automation, a website, software, marketing — or a combination of them.',
    finalCta: 'Talk to MAIPRO',
  },
  fr: {
    eyebrow: 'IA POUR LA CROISSANCE DES ENTREPRISES',
    title: 'Nous construisons des systèmes numériques qui font avancer votre entreprise.',
    intro: "Automatisation, sites web, applications sur mesure et marketing intelligent connectés à vos opérations — pour réduire le travail manuel, améliorer l'expérience client et grandir avec plus de contrôle.",
    primaryCta: 'Parler de votre projet',
    secondaryCta: 'Découvrir les solutions',
    proof: ['La stratégie avant la technologie', 'Conçu autour des opérations réelles', "L'IA là où elle crée de la valeur"],
    problemEyebrow: 'NOUS PARTONS DU PROBLÈME',
    problemTitle: 'Que souhaitez-vous améliorer dans votre entreprise ?',
    problemIntro: "Nous ne commençons pas par un outil. Nous comprenons d'abord ce qui doit mieux fonctionner, puis nous concevons la technologie autour.",
    solutions: [
      { title: 'Automatiser les processus', description: 'Éliminez les tâches répétitives, connectez vos systèmes et créez des flux qui continuent à travailler lorsque votre équipe est occupée.', label: 'Automatisation & IA' },
      { title: 'Créer un site qui vend', description: 'Des sites rapides et clairs, connectés à votre tunnel commercial, CRM, WhatsApp, formulaires et automatisations.', label: 'Sites web' },
      { title: 'Construire une application', description: 'Portails, tableaux de bord et systèmes sur mesure lorsque les outils standards ne suivent plus vos opérations.', label: 'Apps & Systèmes' },
      { title: 'Attirer plus de clients', description: 'Contenu, acquisition payante et décisions marketing structurées par la stratégie, les données et l’exécution.', label: 'Growth & Marketing' },
    ],
    systemEyebrow: 'UN SYSTÈME, PAS DES ÉLÉMENTS ISOLÉS',
    systemTitle: 'Tout peut travailler ensemble.',
    systemIntro: "Marketing, site web, service client et opérations n'ont pas besoin de vivre dans des outils isolés. Lorsque c'est pertinent, nous concevons le flux complet.",
    flow: ['Publicité', 'Site web', 'Lead', 'CRM', 'Automatisation', 'WhatsApp / Email', 'Réservation', 'Follow-up'],
    capabilitiesEyebrow: 'CE QUE NOUS CONSTRUISONS',
    capabilitiesTitle: 'La technologie au service de la croissance et de l’efficacité.',
    capabilities: [
      ['Automatisation & IA', 'Agents, intégrations, service client, follow-up, CRM, workflows internes et opérations assistées par IA.'],
      ['Sites web', 'Sites institutionnels, landing pages, expériences de conversion, SEO technique et intégrations opérationnelles.'],
      ['Apps sur mesure', 'SaaS, portails clients, dashboards, outils internes et applications web adaptées.'],
      ['Growth & Ads', 'Stratégie de contenu, Social Manager, Google Ads, Meta Ads, analyse et amélioration continue.'],
    ],
    productEyebrow: 'PRODUIT MAIPRO',
    productTitle: 'Social Manager. De la stratégie à l’exécution.',
    productDescription: 'Notre propre plateforme organise les objectifs, le contenu, les campagnes, les validations et les décisions marketing dans un seul flux.',
    productPoints: ['Planification guidée par les objectifs', 'Contenu et campagnes dans un même flux', 'Validation avant publication', 'Analyse de performance pour décider de la suite'],
    productCta: 'Découvrir Social Manager',
    casesEyebrow: 'NOTRE FAÇON DE PENSER',
    casesTitle: 'Moins de livrables. Plus de systèmes utiles.',
    cases: [
      ['Acquisition connectée', 'Les publicités et le contenu mènent vers des expériences numériques qui captent, qualifient et orientent chaque opportunité.'],
      ['Opérations automatisées', 'Leads, messages, formulaires, agenda et tâches internes suivent un flux cohérent sans dépendre du travail manuel.'],
      ['Logiciel propriétaire', 'Lorsqu’un outil standard ne résout pas le processus, nous concevons une application autour de l’entreprise.'],
    ],
    processEyebrow: 'NOTRE MÉTHODE',
    processTitle: 'Du problème au système en quatre étapes.',
    process: [
      ['01', 'Comprendre', 'Nous cartographions l’entreprise, l’objectif et les points de friction ou d’opportunité.'],
      ['02', 'Concevoir', 'Nous définissons l’expérience, le flux, les données et la technologie avant de construire.'],
      ['03', 'Construire', 'Nous développons, intégrons et simplifions l’usage pour l’équipe comme pour le client.'],
      ['04', 'Améliorer', 'Nous mesurons l’usage et les résultats pour faire évoluer ce qui compte réellement.'],
    ],
    aiEyebrow: 'NOTRE VISION DE L’IA',
    aiTitle: 'L’IA quand elle a du sens.',
    aiDescription: 'Nous n’ajoutons pas de l’intelligence artificielle simplement pour dire que nous utilisons l’IA. Nous l’appliquons lorsqu’elle accélère un processus, améliore une expérience ou rend une décision plus intelligente.',
    finalTitle: 'Quelque chose dans votre entreprise pourrait mieux fonctionner ?',
    finalDescription: 'Expliquez-nous le problème. Nous vous aiderons à déterminer si la meilleure réponse est l’automatisation, un site, un logiciel, le marketing — ou une combinaison de tout cela.',
    finalCta: 'Parler avec MAIPRO',
  },
} as const;

const iconComponents = [FiZap, FiGlobe, FiCode, FiTrendingUp];

const GrowthHome = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith('en') ? 'en' : i18n.language?.startsWith('fr') ? 'fr' : 'pt';
  const copy = copyByLanguage[language];
  const contactEmail = t('contact.contactInfo.email.value');

  return (
    <main className="growth-home">
      <section className="growth-hero" id="top">
        <div className="growth-shell growth-hero-grid">
          <div className="growth-hero-copy">
            <span className="growth-kicker">{copy.eyebrow}</span>
            <h1>{copy.title}</h1>
            <p className="growth-lead">{copy.intro}</p>
            <div className="growth-actions">
              <a className="growth-button growth-button-dark" href="#contact">
                {copy.primaryCta} <FiArrowUpRight />
              </a>
              <a className="growth-text-link" href="#solutions">
                {copy.secondaryCta} <FiArrowRight />
              </a>
            </div>
            <div className="growth-proof-row">
              {copy.proof.map((item) => (
                <span key={item}><FiCheck /> {item}</span>
              ))}
            </div>
          </div>

          <div className="growth-system-card" aria-label="MAIPRO connected growth system">
            <div className="growth-system-topline">
              <span>MAIPRO / GROWTH SYSTEM</span>
              <span className="growth-live-dot">CONNECTED</span>
            </div>
            <div className="growth-system-main">
              <div className="growth-system-metric">
                <span>Goal</span>
                <strong>Grow without adding operational friction.</strong>
              </div>
              <div className="growth-system-grid">
                <div><small>01</small><strong>ATTRACT</strong><span>Content + Ads</span></div>
                <div><small>02</small><strong>CONVERT</strong><span>Web + CRM</span></div>
                <div><small>03</small><strong>AUTOMATE</strong><span>AI + Workflows</span></div>
                <div><small>04</small><strong>IMPROVE</strong><span>Data + Decisions</span></div>
              </div>
            </div>
            <div className="growth-system-footer">
              <span>Strategy</span><span>Technology</span><span>Execution</span>
            </div>
          </div>
        </div>
      </section>

      <section className="growth-section" id="solutions">
        <div className="growth-shell">
          <div className="growth-section-heading">
            <span className="growth-kicker">{copy.problemEyebrow}</span>
            <h2>{copy.problemTitle}</h2>
            <p>{copy.problemIntro}</p>
          </div>
          <div className="growth-solution-grid">
            {copy.solutions.map((solution, index) => {
              const Icon = iconComponents[index];
              return (
                <article className="growth-solution-card" key={solution.title}>
                  <div className="growth-card-icon"><Icon /></div>
                  <span>{solution.label}</span>
                  <h3>{solution.title}</h3>
                  <p>{solution.description}</p>
                  <a href="#contact" aria-label={`${solution.title} - ${copy.primaryCta}`}><FiArrowUpRight /></a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="growth-section growth-section-soft" id="system">
        <div className="growth-shell">
          <div className="growth-section-heading growth-section-heading-wide">
            <span className="growth-kicker">{copy.systemEyebrow}</span>
            <h2>{copy.systemTitle}</h2>
            <p>{copy.systemIntro}</p>
          </div>
          <div className="growth-flow" role="list">
            {copy.flow.map((step, index) => (
              <div className="growth-flow-step" role="listitem" key={step}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{step}</strong>
                {index < copy.flow.length - 1 && <FiArrowRight className="growth-flow-arrow" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="growth-section" id="capabilities">
        <div className="growth-shell growth-two-column">
          <div className="growth-sticky-heading">
            <span className="growth-kicker">{copy.capabilitiesEyebrow}</span>
            <h2>{copy.capabilitiesTitle}</h2>
          </div>
          <div className="growth-capability-list">
            {copy.capabilities.map(([title, description], index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div><h3>{title}</h3><p>{description}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="growth-product-section" id="social-manager">
        <div className="growth-shell growth-product-grid">
          <div className="growth-product-copy">
            <span className="growth-kicker growth-kicker-light">{copy.productEyebrow}</span>
            <h2>{copy.productTitle}</h2>
            <p>{copy.productDescription}</p>
            <ul>
              {copy.productPoints.map((point) => <li key={point}><FiCheck /> {point}</li>)}
            </ul>
            <a className="growth-button growth-button-light" href="/produtos">
              {copy.productCta} <FiArrowUpRight />
            </a>
          </div>
          <div className="growth-product-ui" aria-label="Social Manager interface preview">
            <div className="growth-product-ui-bar"><span /><span /><span /><strong>Social Manager</strong></div>
            <div className="growth-product-ui-body">
              <aside><b>Today</b><span>Strategy</span><span>Content</span><span>Campaigns</span><span>Insights</span></aside>
              <div className="growth-product-ui-content">
                <div className="growth-product-ui-header"><span>Business goal</span><strong>Turn strategy into consistent execution</strong></div>
                <div className="growth-product-ui-row"><span>Next action</span><b>Review campaign plan</b><small>Ready</small></div>
                <div className="growth-product-ui-row"><span>Content</span><b>3 posts awaiting approval</b><small>Review</small></div>
                <div className="growth-product-ui-row"><span>Performance</span><b>Use results to adjust the next cycle</b><small>Insight</small></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="growth-section" id="approach">
        <div className="growth-shell">
          <div className="growth-section-heading">
            <span className="growth-kicker">{copy.casesEyebrow}</span>
            <h2>{copy.casesTitle}</h2>
          </div>
          <div className="growth-case-grid">
            {copy.cases.map(([title, description], index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="growth-section growth-section-soft" id="process">
        <div className="growth-shell">
          <div className="growth-section-heading">
            <span className="growth-kicker">{copy.processEyebrow}</span>
            <h2>{copy.processTitle}</h2>
          </div>
          <div className="growth-process-grid">
            {copy.process.map(([number, title, description]) => (
              <article key={number}><span>{number}</span><h3>{title}</h3><p>{description}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="growth-ai-section">
        <div className="growth-shell growth-ai-grid">
          <div className="growth-ai-mark"><FiLayers /></div>
          <div>
            <span className="growth-kicker">{copy.aiEyebrow}</span>
            <h2>{copy.aiTitle}</h2>
            <p>{copy.aiDescription}</p>
          </div>
        </div>
      </section>

      <section className="growth-contact" id="contact">
        <div className="growth-shell growth-contact-inner">
          <div>
            <span className="growth-kicker growth-kicker-light">MAIPRO SOLUTIONS</span>
            <h2>{copy.finalTitle}</h2>
            <p>{copy.finalDescription}</p>
          </div>
          <a className="growth-button growth-button-light growth-contact-button" href={`mailto:${contactEmail}`}>
            {copy.finalCta} <FiArrowUpRight />
          </a>
        </div>
      </section>
    </main>
  );
};

export default GrowthHome;
